import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Lock, CheckCircle } from 'lucide-react';
import { agentApi } from '@/services/agentApi';

interface InstitutionData {
  name: string;
  country: string;
  address?: string;
  email?: string;
  website?: string;
  phone?: string;
}

export const TermsAndConditionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [institution, setInstitution] = useState<InstitutionData>({
    name: 'this institution',
    country: 'the country',
    address: '',
    email: '',
    website: '',
    phone: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInstitutionData();
  }, []);

  const loadInstitutionData = async () => {
    const universityId = searchParams.get('universityId');
    
    if (!universityId) {
      setLoading(false);
      return;
    }

    try {
      const targetId = Number(universityId);
      
      // Try direct API call first
      try {
        const university = await agentApi.getUniversityById(targetId);
        if (university) {
          setInstitution({
            name: university.name || 'this institution',
            country: university.country || 'the country',
            address: university.address || university.location?.address || '',
            email: '', // Email not in university model, will use default
            website: university.website || '',
            phone: '' // Phone not in university model
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Error fetching university directly:', err);
      }
      
      // Fallback: try approved institutions
      try {
        const institutions = await agentApi.getApprovedInstitutions();
        const institution = institutions.find(inst => {
          const instId = Number(inst.id);
          return instId === targetId;
        });
        
        if (institution) {
          setInstitution({
            name: institution.name || 'this institution',
            country: institution.country || 'the country',
            address: institution.address || '',
            email: '',
            website: institution.website || '',
            phone: ''
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Error loading approved institutions:', err);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading institution info:', error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Try to close the window/tab if it was opened in a new tab
    // Modern browsers allow closing tabs opened via target="_blank" if opened by user action
    window.close();
    
    // Fallback: if window.close() doesn't work (e.g., main tab), navigate back after a short delay
    setTimeout(() => {
      // If we're still here, the close didn't work, so navigate back
      if (document.visibilityState === 'visible') {
        navigate(-1);
      }
    }, 100);
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8">
          <button
            className="btn btn-link p-0 mb-3"
            onClick={handleClose}
          >
            <ArrowLeft size={20} className="me-2" />
            Back to Application
          </button>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0 fw-bold">
                <FileText size={24} className="me-2" />
                Terms and Conditions - {institution.name} Application Form
              </h3>
            </div>
            <div className="card-body p-4">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading terms and conditions...</p>
                </div>
              ) : (
                <div className="terms-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  
                  {/* Section F: Health, Welfare and Accommodation
                  <section className="mb-5">
                    <h4 className="fw-bold mb-3">
                      <Shield size={20} className="me-2 text-primary" />
                      F. HEALTH, WELFARE AND ACCOMMODATION
                    </h4>
                    
                    <div className="ps-4">
                      <h5 className="fw-semibold mt-4 mb-3">28. Insurance</h5>
                      <p className="mb-3">
                        Travel Insurance is compulsory for all international students studying in {institution.country}.
                        This can be arranged by {institution.name} at your request through our travel insurance provider Southern Cross, or alternative providers.
                        Should you wish to organise it yourself proof of this must be produced prior to arrival into {institution.country}.
                      </p>
                    </div>
                  </section> */}

                  {/* Section G: Important Information */}
                  <section className="mb-5">
                    <h4 className="fw-bold mb-3">
                      <Lock size={20} className="me-2 text-primary" />
                      G. IMPORTANT INFORMATION
                    </h4>
                    
                    <div className="ps-4">
                      <h5 className="fw-semibold mt-4 mb-3">Code of Practice</h5>
                      <p className="mb-3">
                        {institution.name} has agreed to observe and be bound by the Education (Pastoral Care of Tertiary and International Learners) Code of Practice
                        2021. Copies of the Code are available on request from this Institution or from the New Zealand Qualifications Authority{' '}
                        <a href="http://www.nzqa.govt.nz" target="_blank" rel="noopener noreferrer" className="text-primary">http://www.nzqa.govt.nz</a>
                      </p>
                      <p className="mb-4">
                        I have read and understood the Code of Practice<br />
                        Signed: _________________ Date: _________________
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Programme Fees</h5>
                      <p className="mb-3">
                        All international students are required to pay the course fee prior to beginning their programme of study.
                      </p>
                      <ul className="mb-3">
                        <li>Level 4 Total fees = 17,114 - Tuition ($14,000) + Course Costs ($2,250) + Travel and Medical insurance ($864 - 12 months approximately) should you want us to arrange it</li>
                        <li>Level 5 Total fees = 19,200 - Tuition ($16,000) + Course Costs ($2,250) Travel and Medical insurance ($950- 13 months approximately) should you want us to arrange it</li>
                      </ul>
                      <p className="mb-3">
                        *Southern Cross Insurance - quoted 17/07/2024
                      </p>
                      <p className="mb-3">
                        Payments must be made into our Public Trust Account – details will be provided upon final acceptance into the programme.
                        Payments must be made in New Zealand Dollars. If the amount is less than that required, we will not issue a receipt until the correct amount is
                        deposited into the Public Trust Account.
                      </p>
                      <p className="mb-4">
                        I have read and understood the Programme Fees<br />
                        Signed: _________________ Date: _________________
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Refund Policy</h5>
                      <p className="mb-3">
                        {institution.name} has the following refund and reimbursement procedures for the various circumstances that may arise:
                      </p>
                      
                      <h6 className="fw-semibold mt-3 mb-2">Programme of study cancellation</h6>
                      <p className="mb-3">
                        If for any reason we cancel a programme of study (including low enrolments) prior to the commencement date, students will be offered a full reimbursement of
                        the fees paid for the programme of study. In the unlikely event that a course is cancelled after the commencement date students will be offered a pro rata
                        reimbursement of fees within five (5) working days based on the programme of study in which they were enrolled and had paid fees for the time of the closure
                        or cessation.
                      </p>

                      <h6 className="fw-semibold mt-3 mb-2">Closure</h6>
                      <p className="mb-3">
                        In the unlikely event that {institution.name} goes into liquidation, receivership or has its accreditation withdrawn (in which case the Ministry of
                        Education and New Zealand Qualification Authority will be informed immediately) students will be offered the following options by the appointed trustees.
                      </p>
                      <ul className="mb-3">
                        <li>A pro rata reimbursement of fees based on the programme of study in which they were enrolled and had paid fees for at the time of the event,</li>
                        <li>Or A full reimbursement of fees for the programme of study that fees had been paid for but not yet commenced at the time of the event.</li>
                      </ul>
                      <p className="mb-3">
                        The Trust fund and Fees Refund policies and procedures state here comply with the legal requirements set out in section 253 of the Education Act 1989.
                      </p>

                      <h6 className="fw-semibold mt-3 mb-2">International Students Only</h6>
                      <p className="mb-2"><strong>Withdrawal of enrolment before start date:</strong></p>
                      <p className="mb-3">
                        If a student withdraws from a programme of study before the start date of the programme of study, the student will receive total fees paid, less any applicable
                        fees and costs incurred. Such fees and costs must be explained.
                      </p>
                      
                      <p className="mb-2"><strong>For a programme of study less than 5 weeks:</strong></p>
                      <p className="mb-3">
                        If a student withdraws from a programme of study of less than 5 weeks before the end of the 2nd working day of the programme the student will receive at
                        least 50% of the total fees paid. If a student withdraws from a programme of study of less than 5 weeks after the 2nd working day following the first day the
                        student is expected to attend the programme, no refund will be made.
                      </p>
                      
                      <p className="mb-2"><strong>For programme of study longer than 5 weeks but less than 3 months:</strong></p>
                      <p className="mb-3">
                        If a student withdraws before the end of the 5th working day following the first day the student is expected to attend the programme, the student will receive at
                        least 75% of the total fees paid. If a student withdraws after the 5th working day following the first day the student is expected to attend the programme,
                        no refund will be made.
                      </p>
                      
                      <p className="mb-3">
                        No refund of fees will be made after the 10th working day following the first day the student is expected to attend the programme, unless management accepts
                        that there are exceptional circumstances sufficient to warrant a pro rata refund. Refund applications due to exceptional circumstances must be in writing and
                        will be approved on a case by case basis. The student may be required to provide evidence to support their application.
                      </p>
                      <p className="mb-4">
                        I have read and understand the Refund policy<br />
                        Signed: _________________ Date: _________________
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Immigration</h5>
                      <p className="mb-3">
                        Full details of visa and permit requirements, advice on rights to employment in {institution.country} while studying and reporting requirements are available through
                        Immigration {institution.country}, and can be viewed on their website at{' '}
                        <a href="http://www.immigration.govt.nz" target="_blank" rel="noopener noreferrer" className="text-primary">http://www.immigration.govt.nz</a>
                      </p>
                      <p className="mb-4">
                        Students must notify {institution.name}, in writing, of any change of address otherwise they are in breach of their Student Visa and the Code
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Travel and Medical Insurance</h5>
                      <p className="mb-3">
                        International students must have appropriate and current travel and medicine insurance for the duration of their planed period of study. {institution.name}
                        can provide medical and travel insurance through Southern Cross Travel Insurance or alternative provider.
                        Travel Insurance is compulsory for all international students studying at {institution.name}.
                      </p>
                      <p className="mb-3">
                        Would you like {institution.name} to organise your insurance with Southern Cross?<br />
                        Would you like {institution.name} to provide you with a quote from an alternative provider?<br />
                        (Level 4 = 12 months) (Level 5 = 13 months)
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Accident Insurance</h5>
                      <p className="mb-4">
                        The Accident Compensation Corporation (ACC) provides accident insurance for all New Zealand citizens, residents, and temporary visitors to New Zealand, but
                        you may still be liable for all other medical and related costs.
                        Further information can be viewed on the ACC website at{' '}
                        <a href="http://www.acc.co.nz" target="_blank" rel="noopener noreferrer" className="text-primary">http://www.acc.co.nz</a>
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Prior to Starting Programme</h5>
                      <p className="mb-4">
                        For international students, a copy of your Student Visa and Permit must be supplied to the registrar prior to your arrival in {institution.country}. Pastoral Carers are
                        available to assist with all matters relating to your welfare and study while in {institution.country}. {institution.name} has a Pastoral Carer available during
                        course hours.
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Further Information</h5>
                      <p className="mb-4">
                        Further information on courses, fees, entry criteria, facilities, staffing, conditions, frequently asked questions and more can be found in our website at{' '}
                        {institution.website ? (
                          <a href={institution.website} target="_blank" rel="noopener noreferrer" className="text-primary">{institution.website}</a>
                        ) : (
                          <span>our website</span>
                        )}
                      </p>
                    </div>
                  </section>

                  {/* Section H: Declarations and Student Acknowledgements */}
                  <section className="mb-5">
                    <h4 className="fw-bold mb-3">
                      <CheckCircle size={20} className="me-2 text-primary" />
                      H. DECLARATIONS AND STUDENT ACKNOWLEDGEMENTS
                    </h4>
                    
                    <div className="ps-4">
                      <h5 className="fw-semibold mt-4 mb-3">Privacy</h5>
                      <p className="mb-3">
                        {institution.name} collect and store information from this form to comply with the requirements of the Ministry of Education, New Zealand Qualifications Authority, Tertiary
                        Education Commission, Training Organisations, Ministry of Social Development, Department of Immigration and Agencies who support particular students through scholarships and
                        prizes, payment of fees or other awards. The information is also used to select students for qualifications, to manage internal administrative processes, for internal reporting and to
                        provide ancillary services. Information about students may be supplied to, and sought from, other educational institutions for the purpose of verifying academic records. In addition,
                        when required by statute, {institution.name} release information to Government agencies such as the New Zealand Police, Department of Justice, Ministry of Social
                        Development and the Accident Compensation Corporation (ACC).
                      </p>
                      <p className="mb-3">
                        For Communications and Marketing – {institution.name} will use your information to communicate with you and carry out marketing activities. You can unsubscribe from
                        marketing communications through a link provided in marketing emails. {institution.name} will not release your information to other organisations for marketing purposes.
                        In signing this enrolment form you authorise such disclosure on the understanding that {institution.name} will observe the general conditions governing the release of
                        information, as set out in the Privacy Act 2020. You may see any information held about you and amend any errors in that information. To do so, contact your Student Support Advisor.
                      </p>
                      <p className="mb-4">
                        NB: The Privacy Act came into force on 1 July 1993 with the stated aim of protecting the privacy of natural persons It requires the Institution to collect hold, handle, use and disclose
                        personal information in accordance with the twelve information privacy principles in the Act.
                        <a href="https://www.privacy.org.nz/the-privacy-act-2020/privacy-principles/" target="_blank" rel="noopener noreferrer" className="text-primary ms-1">
                          https://www.privacy.org.nz/the-privacy-act-2020/privacy-principles/
                        </a>
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Rules</h5>
                      <p className="mb-4">
                        In signing this enrolment form you undertake to comply with the published rules and policies of {institution.name} with regard to attendance, academic progress, standard of
                        dress, health and safety, behaviour, and to provide {institution.name} with an up-to-date changes to your contact details, next of kin, and residential address.
                      </p>

                      <h5 className="fw-semibold mt-4 mb-3">Student Acknowledgement</h5>
                      <p className="mb-3">
                        {institution.name} wishes to ensure that you understand what will happen to the unused portion of your fees in the unlikely event of a course closure event The
                        unused portion of any fees you pay to {institution.name} is protected by a Trust Fund administered by Public Trust, an independent trustee. By signing this
                        enrolment form, you understand that if your course closes, it will be the trustee's duty to make sure the correct amounts of any refunds owing are distributed in
                        accordance with Ministry of Education and the New Zealand Qualifications Authority Guidelines. You acknowledge and agree that:
                      </p>
                      <ol className="mb-3">
                        <li>If a Course Closure Event occurs and you transfer to an Alternative Provider with the approval of the Qualifications Authority, any amount by you, up to the Entitled
                        Student Amount attributable to you will be transferred from the Trust Fund to the Alternative Provider;</li>
                        <li>If a Course Closure Event occurs and you owe money to a Loan Provider in respect of that Course, the Trustee is authorised to repay the Entitles Student Amount
                        attributable to you, less any amount transferred to an Alternative Provider, directly to the Loan Provider to the extent required to settle the amount due to the Loan
                        Provider;</li>
                        <li>If another party is entitled to receive any refund of the Entitled Student Amount attributable to me, you will provide the Trustee with the contact details of that party to
                        which the refund will be sent.</li>
                        <li>Subject to (b) above, if a Course Closure Event occurs and the Trustee refunds any amount directly to you, the Trustee will refund the Entitled Student Amount
                        attributable to you by way of direct credit to your bank account or cheque posted to your last known postal address notified to that Trustee;</li>
                        <li>Any interest earned on the Trust Fund prior a Course Closure Event will vest in and payable to {institution.name} for its own benefit, and you will have no claim
                        to such interest.</li>
                        <li>Personal information about you and information about your Student Fees may be supplied by {institution.name} to the Qualifications Authority, Auditor, or the
                        Trustee and by the Trustee or Auditor to the Qualifications Authority.</li>
                        <li>The course fee does not cover any Accommodation Expenses, Travel and Health Insurance and Living Expenses.</li>
                        <li>I am/am not 18 years of age or older</li>
                      </ol>

                      <h5 className="fw-semibold mt-4 mb-3">Student Declaration</h5>
                      <p className="mb-3">
                        I confirm that I am enrolling as a student at {institution.name}. I declare that to the best of my knowledge all the information supplied on, and with,
                        this form is true and complete, I agree to abide by the conditions described above, and I consent to the disclosure of person al information as described
                        above.
                      </p>
                      <p className="mb-3">
                        I also agree to the use and disclosure of my information to my parent or student -appointed representative for any purpose related to my education or well-
                        being both before and after admission.
                      </p>
                      <p className="mb-3">
                        I authorize Immigration {institution.country} and the Department of Labour to provide {institution.name} with any personal details regarding my immigration
                        status, including any information that I have submitted to Immigration {institution.country} in the course of any visa or permit application.
                      </p>
                      <p className="mb-3">
                        I understand that I may be required to attend organised {institution.name} excursions and activities as part of my course.
                      </p>
                      <p className="mb-3">
                        I authorise {institution.name} to obtain medical treatment for me should such action be deemed necessary by {institution.name} or a staff
                        member acting on behalf of {institution.name}. I agree to indemnify {institution.name} for any expense, loss, damage, or liability of
                        whatsoever nature as a result of authorising and arranging such emergency medical treatment.
                      </p>
                      <p className="mb-4">
                        I agree that I am responsible for my own books, equipment and personal items and I hereby release {institution.name} from all li ability and claims for
                        loss or damage to such terms.
                      </p>
                      <p className="mb-3">
                      I have read and understood the declaration, student acknowledgement  and other important information on the previous pages of this Enrolment Form.
                      </p>
                      <div className="mb-3">
                        <p className="mb-2"><strong>Full Name:</strong> _________________________________________________________</p>
                        <p className="mb-2"><strong>Signature:</strong> ___________________________________________</p>
                        <p className="mb-2"><strong>Date:</strong> ________________________</p>
                      </div>
                      
                    </div>
                  </section>

                  {/* Contact Information */}
                  <section className="mt-5 pt-4 border-top">
                    <h5 className="fw-semibold mb-3">Contact Information</h5>
                    <p className="mb-2">
                      If you have any questions about these terms and conditions, please contact:
                    </p>
                    <p className="mb-1">
                      <strong>{institution.name}</strong><br />
                      {institution.address && (
                        <>
                          {institution.address}<br />
                        </>
                      )}
                      {institution.email && (
                        <>
                          Email: {institution.email}<br />
                        </>
                      )}
                      {institution.phone && (
                        <>
                          Phone: {institution.phone}<br />
                        </>
                      )}
                      {institution.website && (
                        <>
                          Web: <a href={institution.website} target="_blank" rel="noopener noreferrer" className="text-primary">{institution.website}</a>
                        </>
                      )}
                    </p>
                  </section>

                </div>
              )}
            </div>
            <div className="card-footer bg-light">
              <button
                className="btn btn-primary"
                onClick={handleClose}
              >
                I Understand - Return to Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

